import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { useAuth } from '../App';
import { toast } from 'sonner';
import { Mail, Lock, User, Loader2, Briefcase } from 'lucide-react';
import Logo from '../components/Logo';

const PROFESSIONS_IMMOBILIER = [
  { value: 'agent_immobilier', label: 'Agent immobilier' },
  { value: 'promoteur', label: 'Promoteur immobilier' },
  { value: 'marchand_biens', label: 'Marchand de biens' },
  { value: 'gestionnaire_patrimoine', label: 'Gestionnaire de patrimoine' },
  { value: 'notaire', label: 'Notaire' },
  { value: 'courtier', label: 'Courtier en crédit immobilier' },
  { value: 'architecte', label: 'Architecte' },
  { value: 'expert_immobilier', label: 'Expert immobilier' },
  { value: 'syndic', label: 'Syndic de copropriété' },
  { value: 'diagnostiqueur', label: 'Diagnostiqueur immobilier' },
  { value: 'constructeur', label: 'Constructeur' },
  { value: 'amenageur', label: 'Aménageur foncier' },
  { value: 'autre_pro', label: 'Autre professionnel de l\'immobilier' },
];

const RegisterPage = () => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [status, setStatus] = useState('');
  const [profession, setProfession] = useState('');
  const [charteAccepted, setCharteAccepted] = useState(false);
  const [cgvAccepted, setCgvAccepted] = useState(false);
  const [loading, setLoading] = useState(false);
  const { register } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!status) {
      toast.error('Veuillez sélectionner votre statut');
      return;
    }

    if (status === 'professionnel' && !profession) {
      toast.error('Veuillez sélectionner votre profession');
      return;
    }

    if (!cgvAccepted) {
      toast.error('Vous devez accepter les Conditions Générales de Vente');
      return;
    }

    if (!charteAccepted) {
      toast.error('Vous devez accepter la Charte de Qualité pour créer un compte');
      return;
    }

    if (password !== confirmPassword) {
      toast.error('Les mots de passe ne correspondent pas');
      return;
    }

    if (password.length < 6) {
      toast.error('Le mot de passe doit contenir au moins 6 caractères');
      return;
    }

    setLoading(true);

    try {
      await register(email, password, name, status, profession);
      toast.success('Compte créé avec succès !');
      navigate('/dashboard');
    } catch (error) {
      if (error.response) {
        toast.error(error.response.data?.detail || `Erreur ${error.response.status}`);
      } else if (error.request) {
        toast.error('Impossible de joindre le serveur. Vérifiez votre connexion internet.');
      } else {
        toast.error('Erreur inattendue. Veuillez réessayer.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      {/* Register Form */}
      <div className="flex-1 flex flex-col items-center justify-center px-4 py-12">
        {/* Logo centré comme sur la page Login */}
        <div className="mb-8">
          <Logo size="xxlarge" className="hidden md:flex" />
          <Logo variant="vertical" size="xlarge" className="flex md:hidden" />
        </div>
        <Card className="w-full max-w-md border-slate-200 shadow-lg">
          <CardHeader className="text-center">
            <CardTitle className="text-2xl font-bold text-slate-900">Créer un compte</CardTitle>
            <CardDescription>Rejoignez la communauté des investisseurs</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="name">Nom complet</Label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input
                    id="name"
                    type="text"
                    placeholder="Jean Dupont"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="pl-10"
                    data-testid="register-name-input"
                    required
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input
                    id="email"
                    type="email"
                    placeholder="votre@email.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="pl-10"
                    data-testid="register-email-input"
                    required
                  />
                </div>
              </div>

              {/* Statut */}
              <div className="space-y-2">
                <Label>Vous êtes</Label>
                <Select value={status} onValueChange={setStatus}>
                  <SelectTrigger className="w-full" data-testid="register-status-select">
                    <Briefcase className="h-4 w-4 text-slate-400 mr-2" />
                    <SelectValue placeholder="Sélectionnez votre statut" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="particulier">Particulier (investisseur individuel)</SelectItem>
                    <SelectItem value="professionnel">Professionnel de l'immobilier</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Profession si professionnel */}
              {status === 'professionnel' && (
                <div className="space-y-2">
                  <Label>Votre profession</Label>
                  <Select value={profession} onValueChange={setProfession}>
                    <SelectTrigger className="w-full" data-testid="register-profession-select">
                      <SelectValue placeholder="Sélectionnez votre profession" />
                    </SelectTrigger>
                    <SelectContent>
                      {PROFESSIONS_IMMOBILIER.map((prof) => (
                        <SelectItem key={prof.value} value={prof.value}>
                          {prof.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="password">Mot de passe</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input
                    id="password"
                    type="password"
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="pl-10"
                    data-testid="register-password-input"
                    required
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="confirmPassword">Confirmer le mot de passe</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input
                    id="confirmPassword"
                    type="password"
                    placeholder="••••••••"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="pl-10"
                    data-testid="register-confirm-password-input"
                    required
                  />
                </div>
              </div>

              {/* Acceptation des CGV */}
              <div className="space-y-3 pt-2">
                <div className="flex items-start space-x-3">
                  <Checkbox
                    id="cgv"
                    checked={cgvAccepted}
                    onCheckedChange={setCgvAccepted}
                    data-testid="register-cgv-checkbox"
                  />
                  <div className="grid gap-1.5 leading-none">
                    <label
                      htmlFor="cgv"
                      className="text-sm text-slate-700 cursor-pointer"
                    >
                      J'accepte les{' '}
                      <Link
                        to="/cgv"
                        className="text-[#0D47A1] hover:underline font-medium"
                        onClick={(e) => e.stopPropagation()}
                      >
                        Conditions Générales de Vente
                      </Link>
                    </label>
                  </div>
                </div>

                {/* Acceptation de la charte */}
                <div className="flex items-start space-x-3">
                  <Checkbox
                    id="charte"
                    checked={charteAccepted}
                    onCheckedChange={setCharteAccepted}
                    data-testid="register-charte-checkbox"
                  />
                  <div className="grid gap-1.5 leading-none">
                    <label
                      htmlFor="charte"
                      className="text-sm text-slate-700 cursor-pointer"
                    >
                      J'accepte la{' '}
                      <Link
                        to="/charte"
                        className="text-[#0D47A1] hover:underline font-medium"
                        onClick={(e) => e.stopPropagation()}
                      >
                        Charte de Qualité et de Fonctionnement
                      </Link>
                      {' '}du Club 10 Millions Investor
                    </label>
                  </div>
                </div>

                {/* Texte légal */}
                <p className="text-[10px] text-slate-400 leading-relaxed mt-2">
                  En validant son inscription, le membre reconnaît avoir pris connaissance de la Charte de qualité des membres de la société 10 Millions Investor, accessible sur les supports matériels et immatériels proposés, et l'accepter sans réserve. Il s'engage à en respecter les principes, notamment en matière de comportement, de confidentialité, d'utilisation loyale des services et de respect de l'image du Club. Tout manquement est susceptible d'entraîner des mesures proportionnées pouvant aller jusqu'à la suspension ou la résiliation de l'adhésion, dans les conditions prévues par les Conditions Générales, sous réserve des dispositions légales impératives.
                </p>
              </div>

              <Button
                type="submit"
                className="w-full bg-[#0D47A1] hover:bg-[#0A367A]"
                disabled={loading}
                data-testid="register-submit-btn"
              >
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Création...
                  </>
                ) : (
                  'Créer mon compte'
                )}
              </Button>
            </form>

            <p className="mt-6 text-center text-sm text-slate-600">
              Déjà un compte ?{' '}
              <Link to="/login" className="font-medium text-[#0D47A1] hover:underline">
                Se connecter
              </Link>
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default RegisterPage;
